import { Sentry } from "@/telemetry/sentry"
import { lerp } from "./math"
import { createWebGLContext, ExtendedWebGLContextAttributes } from "@/core/backends/webgl/canvas"

export interface BoxOptions {
	x?: number
	y?: number
	width?: number
	height?: number
	align?: [number, number]
	offset?: number | [number, number]
	padding?: number | [number, number] | [number, number, number, number]
	scale?: [number, number]
	translate?: [number, number]
}

export function drawRect(
	ctx: CanvasRenderingContext2D,
	options: BoxOptions & {
		radius?: number | [number, number, number, number]
		invert?: boolean
		fill?: string | HTMLCanvasElement
		blendMode?: GlobalCompositeOperation
		stroke?: boolean
	} = {},
) {
	const { x, y, width, height } = computeBox(ctx, {
		...options,
	})

	const fillStyle =
		options.fill instanceof HTMLCanvasElement
			? ctx.createPattern(options.fill, "repeat")
			: options.fill

	ctx.fillStyle = fillStyle || "black"
	ctx.globalCompositeOperation = options.blendMode || "source-over"

	ctx.beginPath()

	if (options.radius) {
		let tl = 0
		let tr = 0
		let br = 0
		let bl = 0

		if (typeof options.radius === "number") {
			tl = tr = br = bl = options.radius
		} else if (options.radius.length === 4) {
			tl = options.radius[0]
			tr = options.radius[1]
			br = options.radius[2]
			bl = options.radius[3]
		}

		ctx.moveTo(x + tl, y)
		ctx.lineTo(x + width - tr, y)
		ctx.quadraticCurveTo(x + width, y, x + width, y + tr)
		ctx.lineTo(x + width, y + height - br)
		ctx.quadraticCurveTo(x + width, y + height, x + width - br, y + height)
		ctx.lineTo(x + bl, y + height)
		ctx.quadraticCurveTo(x, y + height, x, y + height - bl)
		ctx.lineTo(x, y + tl)
		ctx.quadraticCurveTo(x, y, x + tl, y)
	} else {
		ctx.rect(x, y, width, height)
	}

	if (options.invert) {
		ctx.rect(ctx.canvas.width, 0, -ctx.canvas.width, ctx.canvas.height)
	}

	ctx.closePath()

	if (options.stroke) {
		ctx.lineWidth = 4
		ctx.strokeStyle = "rgba(255,255,255,0.2)"
		ctx.stroke()
	}

	ctx.fill()
}

function computeBox(ctx: CanvasRenderingContext2D, options: BoxOptions) {
	let x = options.x || 0
	let y = options.y || 0
	let width = options.width || ctx.canvas.width
	let height = options.height || ctx.canvas.height
	let ox = 0
	let oy = 0

	if (options.offset) {
		if (typeof options.offset === "number") {
			ox = oy = options.offset
		} else if (options.offset.length === 2) {
			ox = options.offset[0]
			oy = options.offset[1]
		}
	}

	if (options.align) {
		const left = x + ox
		const right = ctx.canvas.width - width - ox
		const top = y + oy
		const bottom = ctx.canvas.height - height - oy

		x = lerp(left, right, options.align[0])
		y = lerp(top, bottom, options.align[1])
	}

	if (options.padding) {
		let pl = 0
		let pr = 0
		let pt = 0
		let pb = 0

		if (typeof options.padding === "number") {
			pl = pr = pt = pb = options.padding
		} else if (options.padding.length === 2) {
			pt = pb = options.padding[0]
			pl = pr = options.padding[1]
		} else if (options.padding.length === 4) {
			pt = options.padding[0]
			pr = options.padding[1]
			pb = options.padding[2]
			pl = options.padding[3]
		}

		x += pl
		y += pt
		width -= pl + pr
		height -= pt + pb
	}

	if (options.scale) {
		const newWidth = width * options.scale[0]
		const newHeight = height * options.scale[1]

		// x += (width - newWidth) / 2
		// y += (height - newHeight) / 2

		width = newWidth
		height = newHeight
	}

	if (options.translate) {
		x += options.translate[0]
		y += options.translate[1]
	}

	return { x, y, width, height }
}

export function createApplicationWebGLContext(
	options?: ExtendedWebGLContextAttributes,
): WebGL2RenderingContext {
	const canvas = document.createElement("canvas")

	canvas.addEventListener("webglcontextcreationerror", (e) => Sentry.captureException(e))
	canvas.addEventListener("webglcontextlost", (e) =>
		Sentry.captureException(e, { level: "warning" }),
	)

	canvas.addEventListener("webglcontextrestored", (e) =>
		Sentry.captureException(e, { level: "warning" }),
	)

	return createWebGLContext(options, canvas)
}
